import React, { useEffect, useMemo, useState } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'

import { pluralizeToString } from 'lib/pluralize'
import { getCruiseOfferPriceVariation } from 'lib/Cruise/calculatingPrices'
import { CABIN_CATEGORIES } from 'constants/cruise'
import { useIntlDateFormatter, convertTZ } from 'lib/dateUtils'
import { INTL_SHORT_WEEKDAY_COMMA_DAY_SHORT_MONTH_LONG_YEAR } from 'constants/dateFormats'
import Clickable from 'components/Common/Clickable'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Caption from 'components/Luxkit/Typography/Caption'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import LinePricetagAltIcon from 'components/Luxkit/Icons/line/LinePricetagAltIcon'
import Pill from 'components/Luxkit/Pill'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'

const Tile = styled.div`
  display: block;
  align-items: center;
  text-align: left;
  padding: ${rem(20)};
  background: ${({ theme }) => theme.palette.neutral.eight};
  box-shadow: 0px 0.7px 1.4px rgba(0, 0, 0, 0.07),
    0px 1.9px 4px rgba(0, 0, 0, 0.05), 0px 4.5px 10px rgba(0, 0, 0, 0.05);
  border-radius: ${props => props.theme.borderRadius.medium};
  ${mediaQueryUp.sm} {
    display: flex;
  }
`
const Arrow = styled.div`
  justify-content: center;
  width: 100%;
  display: inline-flex;
  align-items: center;
  position: relative;
`

const ArrowLine = styled.div`
  width: 100%;
  background: ${props => props.theme.palette.neutral.one};
  height: 1px;
  float: left;
`

const ArrowPoint = styled.div`
  border: solid ${props => props.theme.palette.neutral.one};
  border-width: 0 1px 1px 0;
  display: inline-block;
  padding: 1px 5px 5px 1px;
  transform: rotate(-45deg);
  -webkit-transform: rotate(-45deg);
  position: absolute;
  right: 0;
`

const Separator = styled.div`
  width: 100%;
  height: ${rem(1)};
  background: ${({ theme }) => theme.palette.neutral.six};
  margin-top: ${rem(22)};
`
const SeparatorHorizontal = styled.div`
  margin: 0 ${rem(22)};
  border-right: 1px solid ${({ theme }) => theme.palette.neutral.six};
  height: 100%;
  min-height: ${rem(150)};
  width: 1%;
  display: none;

  ${mediaQueryUp.sm} {
    display: block;
  }
`

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  flex: none;
  flex-grow: 0;
  max-width: ${rem(96)};

  ${mediaQueryUp.sm} {
    max-width: ${rem(152)};
  }

  > .departure-date {
    font-size: ${rem(18)}
  }
`

const Duration = styled(ContentBox)`
  width: 100%;
  place-items: center;
  max-width: ${rem(152)};
  align-self: center;
  flex: 1 1 auto;
  padding: 0 ${rem(16)};
  text-align: center;

  ${mediaQueryUp.sm} {
    max-width: ${rem(315)};
    align-self: end;
    flex: unset;
    padding: 0;
    text-align: left;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  width: 100%;
  flex-grow: 0;
  position: relative;

  ${mediaQueryUp.sm} {
    position: static;
  }
`

const TileContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${mediaQueryUp.md} {
    width: 79%;
  }
`

const ButtonContainer = styled.div`
  width: 100%;
  margin-top: ${rem(16)};

  ${mediaQueryUp.sm} {
    width: auto;

    > button {
      max-width: ${rem(150)}
    }
  }
`

const ContentRow = styled.div`
  flex-direction: row;
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const CabinPrice = styled(ContentRow)`
  margin-top: 0;
  justify-content: start;
  gap: ${rem(3)};

  > :last-child {
    display: flex;
    align-self: center;
  }
`

const InfoRow = styled.div`
  flex-direction: row;
  display: flex;
  align-items: end;
`

const CabinRow = styled.div`
  flex-wrap: wrap;
  display: flex;
  width: 50%;
  justify-content: space-between;
  & > div:not(:first-child) {
    display: none;
  }

  ${mediaQueryUp.sm} {
    width: 100%;
    & > div:not(:first-child) {
      display: block;
    }
  }
`

const Cabin = styled(VerticalSpacer)`
  overflow: hidden;
  position: relative;

  > :last-child {
    margin-top: 0;
  }
`
const ContainerInfoIcon = styled(Clickable)`
  margin-left: ${rem(4)};
  display: flex;
`

const PromotionDescription = styled.div`
  display: inline-flex;
  gap: ${rem(4)};
  &:empty {
    display: none;
  }
`

type FormattedDates = {
  departure: string;
  arrival: string;
}

interface Props {
  cruise: App.CruiseOffer;
  departure: App.CruiseDepartureDetails;
  onCabinInfoModal: (cabin: App.CabinCategoryType) => void;
  onHandleDepartureModal: (departure: App.CruiseDepartureDetails) => void;
}

function CruiseDepartures(props: Props) {
  const { cruise, departure, onCabinInfoModal, onHandleDepartureModal } = props
  const [cabinFormattedDates, setCabinFormattedDates] = useState<FormattedDates | null>(null)
  const intlDateFormatter = useIntlDateFormatter()

  useEffect(() => {
    if (departure.arrivalDate && departure.departureDate) {
      // Departure and Arrival dates are related to the port timezone, but the timezone is not provided.
      // To enforce the date to stay the same even in negative timezones (such as Americas), we convert the date to UTC
      const arrivalDate = convertTZ(new Date(departure.arrivalDate), 'UTC')
      const arrivalDateFormatted = intlDateFormatter(arrivalDate, INTL_SHORT_WEEKDAY_COMMA_DAY_SHORT_MONTH_LONG_YEAR)

      const departureDate = convertTZ(new Date(departure.departureDate), 'UTC')
      const departureDateFormatted = intlDateFormatter(departureDate, INTL_SHORT_WEEKDAY_COMMA_DAY_SHORT_MONTH_LONG_YEAR)

      setCabinFormattedDates({
        departure: departureDateFormatted,
        arrival: arrivalDateFormatted,
      })
    }
  }, [departure, intlDateFormatter])

  const cabinPrices: App.LowestPricesByCategory = useMemo(() => {
    return cruise.lowestDeparturePricesByCategory[departure.id]
  }, [cruise.lowestDeparturePricesByCategory, departure.id])

  const promotion = useMemo(() => {
    if (!cabinPrices) return null

    const lowestOverallPriceDetails = Object.keys(cabinPrices).reduce((lowest, category) => {
      if (cabinPrices[category].total < lowest.total) return cabinPrices[category]
      return lowest
    }, cabinPrices[Object.keys(cabinPrices)[0]])

    return lowestOverallPriceDetails.promotion ?? null
  }, [cabinPrices])

  return (
    <Tile>
      <TileContainer>
        <ContentContainer>
          <ContentRow>
            <ContentBox>
              <BodyText variant="small" weight="semi-bold">
                {cruise.departurePort}
              </BodyText>
              <BodyText variant="large" weight="bold" className="departure-date">
                {cabinFormattedDates?.departure}
              </BodyText>
            </ContentBox>

            <Duration>
              <ContentRow>
                <Arrow>
                  <ArrowLine className="line" />
                  <ArrowPoint className="point" />
                </Arrow>
              </ContentRow>
              <BodyText variant="medium" weight="bold">
                {pluralizeToString('Night', cruise.duration)}
              </BodyText>
            </Duration>

            <ContentBox>
              <BodyText variant="small" weight="bold">
                {cruise.returnPort}
              </BodyText>
              <BodyText variant="large" weight="bold" className="departure-date">
                {cabinFormattedDates?.arrival}
              </BodyText>
            </ContentBox>
          </ContentRow>
          <PromotionDescription>
            {
              departure.lowestOverallPriceDetails.discountPills?.discountPercentage &&
                <Pill kind="primary" variant="success">
                  Save up to {departure.lowestOverallPriceDetails.discountPills?.discountPercentage}%
                </Pill>
            }
            {promotion?.onBoardCredit && (
              <BodyTextBlock
                startIcon={<LinePricetagAltIcon colour="secondary-normal" />}
                weight="bold"
                variant="medium"
                colour="highlight"
              >
                Up to <FormatCurrency currency={promotion.onBoardCredit.currencyCode} value={promotion.onBoardCredit.amount ?? 0} /> onboard credit available
              </BodyTextBlock>
            )}
          </PromotionDescription>
        </ContentContainer>

        <ContentContainer>
          <Separator />

          <CabinRow>
            {CABIN_CATEGORIES.map((item) => {
              if (cabinPrices && cabinPrices[item.category.toLowerCase()]) {
                const cabinPrice = cabinPrices[item.category.toLowerCase()]
                const {
                  primaryPrice,
                  secondaryPrice,
                } = getCruiseOfferPriceVariation(cruise, cabinPrice)

                return (
                  <Cabin key={item.category} gap={4}>
                    <InfoRow>
                      <BodyText variant="small" weight="normal">
                        {item.category} from
                      </BodyText>
                      <ContainerInfoIcon
                        onClick={() => onCabinInfoModal(item.category as App.CabinCategoryType)}
                      >
                        <LineInfoCircleIcon size="S" />
                      </ContainerInfoIcon>
                    </InfoRow>
                    <CabinPrice className="cabin-price">
                      <BodyText variant="large" weight="bold">
                        <FormatCurrency
                          value={primaryPrice.total ?? 0}
                        />
                      </BodyText>
                      <BodyText variant="medium" colour="neutral-one">
                        /{primaryPrice.type}
                      </BodyText>
                    </CabinPrice>

                    {secondaryPrice && <CabinPrice>
                      <Caption variant="large" colour="neutral-three" as="spam">
                        <FormatCurrency value={secondaryPrice.total ?? 0} />
                      </Caption>
                      <Caption variant="large" colour="neutral-three" as="spam">
                        /{secondaryPrice.type}
                      </Caption>
                    </CabinPrice>}

                    <BodyText variant="small" colour="neutral-two">
                      Twin Share
                    </BodyText>
                  </Cabin>
                )
              }
            })}
          </CabinRow>
        </ContentContainer>
      </TileContainer>

      <SeparatorHorizontal />

      <VerticalSpacer gap={12}>
        <ButtonContainer>
          <TextButton
            kind="primary"
            fit="full-width"
            onClick={() => onHandleDepartureModal(departure)}
          >
            Select departure
          </TextButton>
        </ButtonContainer>
      </VerticalSpacer>
    </Tile>
  )
}

export default CruiseDepartures
